// validateService.js - VALIDATE feature service
// DriftAlarm.AI - Built by Security Experts
// Supports dual-mode architecture: Standalone and Integrated (from SCRUTINIZE/Asset)

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

// Default validation types - all enabled by default
export const DEFAULT_VALIDATION_TYPES = {
    xss: true,
    sqli: true,
    defaultCreds: true,
    anonymousFtp: true,
    anonymousMongo: true,
    anonymousRedis: true,
    anonymousElastic: true,
    smtpRelay: true,
    cors: true,
    openRedirect: true,
    securityHeaders: true,
    sslTls: true,
    subdomainTakeover: true,
    directoryListing: true,
    sensitiveFiles: true
};

// Validation type metadata for UI
export const VALIDATION_TYPE_INFO = {
    xss: { label: 'XSS Reflection', group: 'Web Application Tests', icon: '🌐' },
    sqli: { label: 'SQL Injection', group: 'Web Application Tests', icon: '🌐' },
    cors: { label: 'CORS Misconfiguration', group: 'Web Application Tests', icon: '🌐' },
    openRedirect: { label: 'Open Redirects', group: 'Web Application Tests', icon: '🌐' },
    securityHeaders: { label: 'Security Headers', group: 'Web Application Tests', icon: '🌐' },
    directoryListing: { label: 'Directory Listing', group: 'Web Application Tests', icon: '🌐' },
    sensitiveFiles: { label: 'Sensitive Files', group: 'Web Application Tests', icon: '🌐' },
    defaultCreds: { label: 'Default Credentials', group: 'Authentication Tests', icon: '🔐' },
    anonymousFtp: { label: 'Anonymous FTP', group: 'Service Tests', icon: '🖥️' },
    anonymousMongo: { label: 'Anonymous MongoDB', group: 'Service Tests', icon: '🖥️' },
    anonymousRedis: { label: 'Anonymous Redis', group: 'Service Tests', icon: '🖥️' },
    anonymousElastic: { label: 'Anonymous Elasticsearch', group: 'Service Tests', icon: '🖥️' },
    smtpRelay: { label: 'Open SMTP Relay', group: 'Service Tests', icon: '🖥️' },
    sslTls: { label: 'SSL/TLS Issues', group: 'Infrastructure Tests', icon: '🏗️' },
    subdomainTakeover: { label: 'Subdomain Takeover', group: 'Infrastructure Tests', icon: '🏗️' }
};

/**
 * Helper to get auth token
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Get headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * Start a VALIDATE job - supports both standalone and integrated modes
 * @param {object} options - Validation options
 * @param {string} options.target - Target domain/IP (required for standalone)
 * @param {string} options.mode - 'standalone' | 'integrated'
 * @param {string} options.sourceType - 'scrutinize' | 'asset' | null
 * @param {string} options.sourceScanId - SCRUTINIZE scan ID (for integrated mode)
 * @param {string} options.sourceAssetId - Asset ID (for integrated mode)
 * @param {object} options.validationTypes - Checkbox selections
 * @param {object} options.inheritedData - Pre-populated data from source
 * @returns {Promise<object>} - { success, jobId, message }
 */
export async function startValidation(options) {
    const {
        target,
        mode = 'standalone',
        sourceType = null,
        sourceScanId = null,
        sourceAssetId = null,
        validationTypes = DEFAULT_VALIDATION_TYPES,
        inheritedData = null
    } = options;

    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/validate-start`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                target,
                mode,
                sourceType,
                sourceScanId,
                sourceAssetId,
                validationTypes,
                inheritedData
            }),
        });

        const data = await response.json();

        if (!response.ok) {
            const error = new Error(data.error || data.message || 'Failed to start validation');
            error.statusCode = response.status;
            error.upgradeRequired = data.upgradeRequired;
            error.limit = data.limit;
            error.used = data.used;
            throw error;
        }

        return data;
    } catch (error) {
        console.error('Error starting VALIDATE:', error);
        throw error;
    }
}

/**
 * Legacy: Trigger a VALIDATE job for vulnerability validation
 * @deprecated Use startValidation instead
 * @param {object} options - { assetId, scanId, findings }
 * @returns {Promise<object>} - { success, jobId, message }
 */
export async function triggerValidate(options) {
    const { assetId, scanId, findings } = options;

    // Convert legacy format to new format
    return startValidation({
        target: options.target,
        mode: 'integrated',
        sourceType: assetId ? 'asset' : 'scrutinize',
        sourceScanId: scanId,
        sourceAssetId: assetId,
        inheritedData: { findings }
    });
}

/**
 * Get VALIDATE job status
 * @param {string} jobId - The validation job ID
 * @returns {Promise<object>} - Job status info
 */
export async function getValidateStatus(jobId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/validate-status/${jobId}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get status (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting VALIDATE status:', error);
        throw error;
    }
}

/**
 * Get VALIDATE results
 * @param {string} jobId - The validation job ID
 * @returns {Promise<object>} - Validation results
 */
export async function getValidateResults(jobId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/validate-result/${jobId}`, {
            method: 'GET',
            headers,
        });

        if (response.status === 202) {
            // Results not ready yet
            return { ready: false, ...(await response.json()) };
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get results (${response.status})`);
        }

        return { ready: true, ...(await response.json()) };
    } catch (error) {
        console.error('Error getting VALIDATE results:', error);
        throw error;
    }
}

/**
 * Poll for VALIDATE completion
 * @param {string} jobId - The validation job ID
 * @param {function} onProgress - Progress callback
 * @param {number} maxWaitMs - Maximum wait time (default 30 minutes)
 * @returns {Promise<object>} - Final results
 */
export async function pollValidateCompletion(jobId, onProgress, maxWaitMs = 1800000) {
    const startTime = Date.now();
    const pollInterval = 5000; // 5 seconds

    while (Date.now() - startTime < maxWaitMs) {
        try {
            const status = await getValidateStatus(jobId);

            if (onProgress) {
                onProgress(status);
            }

            if (status.status === 'complete') {
                return await getValidateResults(jobId);
            }

            if (status.status === 'failed') {
                throw new Error(status.error || 'Validation failed');
            }

            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, pollInterval));

        } catch (error) {
            console.error('Poll error:', error);
            throw error;
        }
    }

    throw new Error('Validation timed out');
}

/**
 * Extract validatable findings from scan results
 * @param {object} scanResult - Scan result object (from Fast Scan or SCRUTINIZE)
 * @returns {Array} - Array of findings suitable for validation
 */
export function extractValidatableFindings(scanResult) {
    const findings = [];

    // Extract from nuclei results
    if (scanResult.nuclei?.findings) {
        for (const finding of scanResult.nuclei.findings) {
            // Only validate high/critical severity
            if (['critical', 'high'].includes(finding.severity?.toLowerCase())) {
                findings.push({
                    source: 'nuclei',
                    name: finding.name,
                    severity: finding.severity,
                    url: finding.matched || finding.host,
                    templateId: finding.templateId || finding['template-id'],
                    description: finding.description,
                    tags: finding.tags
                });
            }
        }
    }

    // Extract from asset scan results (stageResults.vulnScan)
    if (scanResult.stageResults?.vulnScan?.findings) {
        for (const finding of scanResult.stageResults.vulnScan.findings) {
            if (['critical', 'high'].includes(finding.severity?.toLowerCase())) {
                findings.push({
                    source: 'asset-vuln-scan',
                    name: finding.name,
                    severity: finding.severity,
                    url: finding.matched || finding.host,
                    templateId: finding.templateId,
                    description: finding.description,
                    tags: finding.tags
                });
            }
        }
    }

    // Remove duplicates based on URL + name
    const seen = new Set();
    return findings.filter(f => {
        const key = `${f.url}|${f.name}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

/**
 * Extract inherited data from SCRUTINIZE scan results for integrated mode
 * @param {object} scanResult - SCRUTINIZE scan result
 * @returns {object} - Inherited data for validation
 */
export function extractScrutinizeData(scanResult) {
    const data = {
        target: scanResult.target,
        subdomains: [],
        ips: [],
        liveUrls: [],
        openPorts: [],
        adminPanels: [],
        loginForms: [],
        mxRecords: [],
        crawledUrls: [],
        technologies: []
    };

    // Extract reconnaissance data
    if (scanResult.reconnaissance) {
        data.subdomains = scanResult.reconnaissance.subdomains || [];
        data.ips = scanResult.reconnaissance.ipAddresses || [];
        if (scanResult.reconnaissance.dnsRecords?.mx) {
            data.mxRecords = scanResult.reconnaissance.dnsRecords.mx.map(r => r.value || r);
        }
    }

    // Extract port scan data
    if (scanResult.portScan) {
        data.openPorts = (scanResult.portScan.openPorts || []).map(p => ({
            ip: p.ip || p.host,
            port: p.port,
            service: p.service || 'unknown'
        }));
    }

    // Extract web analysis data
    if (scanResult.webAnalysis) {
        data.liveUrls = (scanResult.webAnalysis.urls || []).map(u => ({
            url: typeof u === 'string' ? u : u.url,
            status: u.status || 200,
            title: u.title || ''
        }));
        data.technologies = scanResult.webAnalysis.technologies || [];
        data.adminPanels = scanResult.webAnalysis.adminPanels || [];
        data.loginForms = scanResult.webAnalysis.forms || [];
        data.crawledUrls = scanResult.webAnalysis.crawledEndpoints || [];
    }

    return data;
}

/**
 * Extract inherited data from Asset baseline results for integrated mode
 * @param {object} assetResult - Asset scan result with stageResults
 * @returns {object} - Inherited data for validation
 */
export function extractAssetData(assetResult) {
    const stageResults = assetResult.stageResults || {};
    const data = {
        target: assetResult.assetValue || assetResult.target,
        subdomains: [],
        ips: [],
        liveUrls: [],
        openPorts: [],
        adminPanels: [],
        loginForms: [],
        mxRecords: [],
        crawledUrls: [],
        technologies: []
    };

    // Stage 1: DNS Enumeration
    if (stageResults.dnsEnumeration) {
        data.subdomains = stageResults.dnsEnumeration.subdomains || [];
        data.liveSubdomains = stageResults.dnsEnumeration.liveSubdomains || [];
    }

    // Stage 2: IP Resolution
    if (stageResults.ipResolution) {
        data.ips = stageResults.ipResolution.ips || [];
    }

    // Stage 3: Port Scanning
    if (stageResults.portScanning) {
        data.openPorts = stageResults.portScanning.openPorts || [];
    }

    // Stage 4: Web Crawling
    if (stageResults.webCrawling) {
        data.liveUrls = (stageResults.webCrawling.urls || []).map(u => ({
            url: typeof u === 'string' ? u : u.url,
            status: 200
        }));
        data.technologies = stageResults.webCrawling.technologies || [];
        data.crawledUrls = stageResults.webCrawling.urls || [];
    }

    // Stage 5: Vuln Scanning - extract vuln data if needed
    if (stageResults.vulnScanning) {
        data.vulnerabilities = stageResults.vulnScanning.vulnerabilities || [];
    }

    return data;
}

/**
 * Calculate target counts for each validation type based on discovered data
 * @param {object} discoveredData - Inherited or discovered data
 * @returns {object} - Counts per validation type
 */
export function calculateTargetCounts(discoveredData) {
    const counts = {};
    const urls = discoveredData.crawledUrls || discoveredData.liveUrls || [];
    const urlsWithParams = urls.filter(u => {
        const url = typeof u === 'string' ? u : u.url;
        return url && url.includes('?');
    });

    counts.xss = urls.length;
    counts.sqli = urlsWithParams.length;
    counts.cors = discoveredData.liveUrls?.length || 0;
    counts.openRedirect = urls.length;
    counts.securityHeaders = discoveredData.liveUrls?.length || 0;
    counts.directoryListing = discoveredData.liveUrls?.length || 0;
    counts.sensitiveFiles = discoveredData.liveUrls?.length || 0;
    counts.defaultCreds = (discoveredData.adminPanels?.length || 0) + (discoveredData.loginForms?.length || 0);
    counts.anonymousFtp = (discoveredData.openPorts || []).filter(p => p.port === 21).length;
    counts.anonymousMongo = (discoveredData.openPorts || []).filter(p => p.port === 27017).length;
    counts.anonymousRedis = (discoveredData.openPorts || []).filter(p => p.port === 6379).length;
    counts.anonymousElastic = (discoveredData.openPorts || []).filter(p => p.port === 9200).length;
    counts.smtpRelay = discoveredData.mxRecords?.length || 0;
    counts.sslTls = (discoveredData.liveUrls || []).filter(u => {
        const url = typeof u === 'string' ? u : u.url;
        return url && url.startsWith('https');
    }).length;
    counts.subdomainTakeover = discoveredData.subdomains?.length || 0;

    return counts;
}

/**
 * Get download URL for raw results archive (ZIP)
 * @param {string} jobId - The validation job ID
 * @returns {Promise<object>} - { downloadUrl, filename, expiresIn }
 */
export async function downloadRawResults(jobId) {
    try {
        const headers = await getHeaders();

        const response = await fetch(`${API_BASE_URL}/validate-download/${jobId}`, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Failed to get download URL (${response.status})`);
        }

        return await response.json();
    } catch (error) {
        console.error('Error getting download URL:', error);
        throw error;
    }
}

// Named export for { validateService } imports
export const validateService = {
    startValidation,
    triggerValidate, // Legacy
    getValidateStatus,
    getValidateResults,
    getValidateResult: getValidateResults, // Alias for compatibility
    pollValidateCompletion,
    downloadRawResults,
    extractValidatableFindings,
    extractScrutinizeData,
    extractAssetData,
    calculateTargetCounts,
    DEFAULT_VALIDATION_TYPES,
    VALIDATION_TYPE_INFO
};

// Default export
export default validateService;
